import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

interface Role {
  id: string;
  name: string;
  code: string;
}

const UserForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    roleId: '',
    password: '',
    isActive: true,
  });

  const [autoGeneratePassword, setAutoGeneratePassword] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  
  // Delete State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
    if (isEdit) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/system-settings/roles');
      setRoles(response.data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`/system-settings/users/${id}`);
      const user = response.data;
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        mobile: user.phone || '',
        roleId: user.roleId,
        password: '',
        isActive: user.isActive,
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to load user details');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        mobile: formData.mobile,
        roleId: formData.roleId,
      };

      if (!isEdit) {
        if (!autoGeneratePassword) {
          payload.password = formData.password;
        }
      }
      
      if (isEdit) {
        await axios.put(`/system-settings/users/${id}`, payload);
        navigate('/system-settings/user-roles/users');
      } else {
        await axios.post('/system-settings/users', payload);
        navigate('/system-settings/user-roles/users');
      }
    } catch (err: any) {
      console.error('Error saving user:', err.response?.data);
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/system-settings/users/${id}`);
      navigate('/system-settings/user-roles/users');
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading && isEdit) {
    return <MainLayout><Container><Typography>Loading...</Typography></Container></MainLayout>;
  }

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/system-settings/user-roles/users')}>
            Back
          </Button>
          <Typography variant="h4">
            {isEdit ? 'Edit User' : 'Create New User'}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.roleId}
                    label="Role"
                    onChange={(e) => setFormData({ ...formData, roleId: e.target.value as string })}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Active Toggle removed as per business rule */}

              {!isEdit && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={autoGeneratePassword}
                          onChange={(e) => setAutoGeneratePassword(e.target.checked)}
                        />
                      }
                      label="Auto-generate password"
                    />

                    {!autoGeneratePassword && (
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        required={!autoGeneratePassword}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        sx={{ mt: 2 }}
                      />
                    )}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  {isEdit && (
                    <Button 
                      variant="outlined" 
                      color="error" 
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      Delete User
                    </Button>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
                    <Button variant="outlined" onClick={() => navigate('/system-settings/user-roles/users')}>
                      Cancel
                    </Button>
                    <Button variant="contained" type="submit" startIcon={<SaveIcon />} size="large">
                      Save User
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this user?
              <br /><br />
              This action cannot be undone. If the user has activity history, they will be deactivated instead.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={deleteLoading}>
              {deleteLoading ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </MainLayout>
  );
};

export default UserForm;
